<script setup lang="ts">
import { ref, computed, watch } from "vue";
import ManualSelectionService from "@/services/manualSelection/manualSelectionService";
import ServerAutocomplete, {
  type FetchPageArgs,
  type Option,
} from "@/components/ServerAutocomplete.vue";
import { useI18n } from "vue-i18n";

type ManualSelectionPayload = {
  serial_number: string;
  brand_id: string;
  product_id: string;
};

const { t } = useI18n();

const emit = defineEmits<{
  (e: "confirm", payload: ManualSelectionPayload): void;
}>();

const serialNumber = ref("");

const brand = ref<Option | null>(null);
const product = ref<Option | null>(null);

const isValid = computed(() => {
  return (
    serialNumber.value.trim().length > 0 &&
    !!brand.value &&
    !!product.value
  );
});

watch(brand, () => {
  product.value = null;
});

const confirm = () => {
  if (!isValid.value) return;

  emit("confirm", {
    serial_number: serialNumber.value.trim(),
    brand_id: brand.value!.id,
    product_id: product.value!.id,
  });
};

const fetchBrands = async ({ page, pageSize, search }: FetchPageArgs) => {
  const response = await ManualSelectionService.getBrands({
    page,
    page_size: pageSize,
    search,
  });
  return {
    items: response.items,
    hasMore: response.hasMore,
    total: response.total,
  };
};

const fetchProducts = async ({ page, pageSize, search }: FetchPageArgs) => {
  if (!brand.value) return { items: [], hasMore: false };
  const response = await ManualSelectionService.getProducts(brand.value.id, {
    page,
    page_size: pageSize,
    search,
  });
  return {
    items: response.items,
    hasMore: response.hasMore,
    total: response.total,
  };
};

</script>

<template>
  <div class="manual-sheet">
    <h2 class="sheet-title">
      {{ t("views.captureView.manual.selection") }}
    </h2>

    <v-text-field
      v-model="serialNumber"
      :label="t('views.captureView.manual.serialNumber')"
      variant="outlined"
      density="comfortable"
      rounded="lg"
      hide-details="auto"
    />

    <ServerAutocomplete
      v-model="brand"
      :label="t('views.captureView.manual.manufacturer')"
      :fetch-page="fetchBrands"
      prepend-icon="mdi-factory"
      :placeholder="t('views.captureView.manual.searchManufacturer')"
    />

    <ServerAutocomplete
      v-model="product"
      :label="t('views.captureView.manual.product')"
      :disabled="!brand"
      :fetch-page="fetchProducts"
      prepend-icon="mdi-cube-outline"
      :placeholder="t('views.captureView.manual.searchProduct')"
      :key="brand?.id || 'no-brand'"
    />

    <v-btn
      block
      class="btn-primary btn-pill mt-2"
      :disabled="!isValid"
      @click="confirm"
    >
      {{ t("views.captureView.manual.confirm") }}
    </v-btn>
  </div>
</template>

<style scoped>
.manual-sheet {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.sheet-title {
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 4px;
}

.btn-pill {
  border-radius: 999px;
  height: 48px;
  font-weight: 700;
}
</style>
